import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  LabelList,
  LabelProps,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Cell,
} from 'recharts';
import {
  BAR_CHART_MIN_POINT_SIZE,
  BAR_CHART_STROKE_WIDTH,
  CHART_MARGIN,
  HISTOGRAM_LABEL,
} from '../constants/utils';
import CustomXAxisTick from '../CustomXAxisTick';
import CustomYAxisTick from '../CustomYAxisTick';
import {
  useFormattedChartData,
  usePrepareCohort,
  useSingleChartDataTooltip,
  useSingleDateData,
  useSortLines,
  useYAxisBounds,
  useYAxisFormat,
  useYAxisWidth,
} from '../hooks';
import { assertCondition, assertNotNil } from '../../../utils/types';
import {
  ExpressionModeKey,
  QueryType,
  TimeUnit,
} from '../../../utils/apollo/graphqlSchema';
import { generateCustomLabels } from '../BarChart/generateCustomLabels';
import { getMeasureFormatByColumnKey } from '../CustomTooltip/utils';
import { YAxisPosition } from '../interfaces';
import ResponsiveChartWrapper from '../ResponsiveChartWrapper';
import useXAxisSettings from '../utils/xAxis/useXAxisSettings';
import { ChartMode } from '../types';
import calculateBarSize from '../utils/calculateBarSize';
import SingleChartDataTooltip from '../SingleChartDataTooltip';
import StyledBar from '../BarChart/StyledBar';
import useCacheSingleDateLlmRawDataEffect from '../hooks/llmCacheRawData/useCacheSingleDateLlmRawDataEffect';
import useChartLines from '../hooks/useChartLines';
import SingleDayChartWrapper from './SingleDayChartWrapper';
import { useRef } from 'react';
import { useChartContext } from '../ChartContext';
interface IProps {
  analysisId: string;
  mode: ChartMode;
}
export default ({ analysisId, mode }: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    settings: { chartWidth, chartSettings },
    metadata: { isBreakdownOverall, timeUnit, query },
    chartData,
    colorConfig,
    displayedMeasures,
  } = useChartContext();
  assertCondition(query.__typename === 'ExpressionQuery');
  const isHistogram = query.expressionMode.key === ExpressionModeKey.Histogram;

  // narrow down chartData to IParsedChartDataExpression
  assertCondition(chartData.queryType === QueryType.Expression);
  const { data, linesMeta, dataKeysOnMeasure } = chartData;
  const { singleDayLabel, sort, chartType, axisSettings } = chartSettings;

  const {
    tooltipProps,
    isTooltipLocked,
    onBarMouseMove,
    onMouseLeave,
    lockTooltipPositionHandler,
    clearLockedTooltipProps,
  } = useSingleChartDataTooltip({
    containerRef,
  });
  const { handleCreateCohortFromExpression } = usePrepareCohort({ analysisId });
  const { visibleLines } = useChartLines({
    chartSettings,
    chartData,
    isBreakdownOverall,
    colorConfig,
  });

  const { getYAxisDomain } = useYAxisBounds();

  const currentFormat = useYAxisFormat({
    displayedMeasures,
  });
  const formattedChartData = useFormattedChartData({
    chartData: data,
    displayedMeasures,
    dataKeysOnMeasure,
  });
  const { parsedData } = useSingleDateData({
    data: formattedChartData,
    visibleLines,
    singleDayLabel,
    linesMeta,
    ignoreOverall: false,
    chartType,
    measureControls: chartData.measureControls,
    displayedMeasures,
    queryType: QueryType.Expression,
    timeUnit,
  });

  const [sortedVisibleLines, sortedData] = useSortLines({
    visibleLines,
    sort,
    data: parsedData,
  });
  const numItems = visibleLines.length;
  const barSize = calculateBarSize(numItems, chartWidth);

  const xAxisSettings = useXAxisSettings({
    barSize,
    numItems,
    chartWidth,
    chartMode: mode,
  });

  useCacheSingleDateLlmRawDataEffect({
    analysisId,
    visibleLines: sortedVisibleLines,
    data: sortedData,
    chartTypeName: isHistogram ? 'Histogram' : 'Single Day Bar',
    singleDayLabel: isHistogram ? null : singleDayLabel,
  });

  // Calculate the Y axis width dynamically
  const yAxisWidth = useYAxisWidth();

  return (
    <SingleDayChartWrapper
      analysisId={analysisId}
      ignoreCloseButton={isHistogram && timeUnit === TimeUnit.AllTime}
    >
      <ResponsiveChartWrapper analysisId={analysisId} mode={mode}>
        <ResponsiveContainer ref={containerRef}>
          <RechartsBarChart
            data={sortedData}
            margin={CHART_MARGIN}
            barCategoryGap={'12%'}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              {...xAxisSettings}
              dataKey={HISTOGRAM_LABEL}
              tick={({ ...props }) => {
                return (
                  <CustomXAxisTick
                    {...props}
                    barSize={barSize}
                    isAllowLabelRotated={xAxisSettings.shouldRotateXAxis}
                    isTruncateLabel={true}
                  />
                );
              }}
            />

            <YAxis
              padding={{ top: 3, bottom: 0 }}
              hide={!axisSettings.yAxisSettings[YAxisPosition.LEFT].isVisible}
              tickSize={0}
              domain={getYAxisDomain({
                yAxisPosition: YAxisPosition.LEFT,
                visibleLines,
              })}
              allowDataOverflow={true}
              axisLine={false} // Whether to display the Y axis vertical line or not
              tick={<CustomYAxisTick measureDisplayFormat={currentFormat} />}
              tickCount={5}
              tickMargin={10}
              width={yAxisWidth}
            />

            <Bar
              isAnimationActive={false}
              dataKey={'value'}
              strokeDasharray={'1 0'}
              minPointSize={BAR_CHART_MIN_POINT_SIZE}
              strokeWidth={BAR_CHART_STROKE_WIDTH}
              barSize={barSize}
              onMouseMove={(eventProps, index, mouseElement) => {
                onBarMouseMove({
                  rechartProps: {
                    ...eventProps,
                    date: singleDayLabel,
                    xAxisKey: 'value',
                  },
                  line: {
                    ...eventProps.payload,
                    measureKey: sortedVisibleLines[index].measureKey,
                  },
                  mouseEvent: mouseElement,
                });
              }}
              onMouseLeave={onMouseLeave}
              onClick={lockTooltipPositionHandler}
              shape={<StyledBar />}
            >
              {sortedVisibleLines.map((line, index) => (
                <Cell
                  key={`cell-${index}`}
                  stroke={line.type}
                  shapeRendering="crispEdges"
                  fill={line.color}
                  opacity={
                    !tooltipProps || tooltipProps.line.name === line.name
                      ? 1
                      : 0.3
                  }
                />
              ))}
              <LabelList
                dataKey={'value'}
                content={(untypedProps) => {
                  const props = untypedProps as LabelProps & { index: number };
                  const lineKey = sortedData[props.index].name;
                  const line = sortedVisibleLines.find(
                    (vl) => vl.name === lineKey,
                  );

                  assertNotNil(line);
                  return generateCustomLabels(
                    sortedData,
                    props,
                    line,
                    getMeasureFormatByColumnKey({
                      columnKey: lineKey,
                      linesMeta,
                      displayedMeasures,
                    }),
                  );
                }}
              />
            </Bar>
          </RechartsBarChart>
        </ResponsiveContainer>
      </ResponsiveChartWrapper>
      {tooltipProps && (
        <SingleChartDataTooltip
          analysisId={analysisId}
          handleCreateCohort={handleCreateCohortFromExpression}
          tooltipProps={tooltipProps}
          isTooltipLocked={isTooltipLocked}
          clearLockedTooltipProps={clearLockedTooltipProps}
        />
      )}
    </SingleDayChartWrapper>
  );
};
